<template>
  <div>
    <div class="d-flex align-items-center justify-content-between mb-4">
      <div class="d-flex align-items-center">
        <CButton
          color="link"
          class="p-0 mr-3"
          @click="$router.push({ name: 'customer_success.search' })"
        >
          <CIcon name="cil-arrow-left" size="lg"/>
        </CButton>
        <h1 class="main-header mb-0">
          {{ user ? user.email : `#${$route.params.id}` }}
        </h1>
      </div>
      <CButton
        v-if="canImpersonate && user"
        color="warning"
        @click="onImpersonate"
      >
        <CIcon name="cil-user" size="sm" class="mr-1"/>
        <translate translate-context="customer_success.user_detail">Impersonate</translate>
      </CButton>
    </div>

    <div v-if="user">
      <!-- First Row: User Info & Account Settings -->
      <CRow class="mb-4">
        <!-- User Info (read-only) -->
        <CCol lg="5" md="6" class="mb-3">
          <CCard class="h-100">
            <CCardHeader>
              <CIcon name="cil-user" size="sm" class="mr-2"/>
              <translate translate-context="customer_success.user_detail">User Info</translate>
            </CCardHeader>
            <CCardBody>
              <div class="detail-row">
                <span class="detail-label">ID</span>
                <span class="detail-value">{{ user.id }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Email</span>
                <span class="detail-value">{{ user.email }}</span>
              </div>
              <div v-if="user.full_name" class="detail-row">
                <span class="detail-label">
                  <translate translate-context="customer_success.user_detail">Name</translate>
                </span>
                <span class="detail-value">{{ user.full_name }}</span>
              </div>
              <div v-if="user.language" class="detail-row">
                <span class="detail-label">
                  <translate translate-context="customer_success.user_detail">Language</translate>
                </span>
                <span class="detail-value">{{ user.language }}</span>
              </div>
              <div v-if="user.timezone" class="detail-row">
                <span class="detail-label">Timezone</span>
                <span class="detail-value">{{ user.timezone }}</span>
              </div>
              <div v-if="user.date_joined" class="detail-row">
                <span class="detail-label">
                  <translate translate-context="customer_success.user_detail">Joined</translate>
                </span>
                <span class="detail-value">{{ formatDate(user.date_joined) }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">
                  <translate translate-context="customer_success.user_detail">Last Login</translate>
                </span>
                <span class="detail-value">{{ user.last_login ? formatDate(user.last_login) : '-' }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Self Service</span>
                <span class="detail-value">
                  <template v-if="user.is_saas_user && user.saas_contract_manager">
                    <div
                      v-for="cm in saasContractManagers"
                      :key="cm.id"
                    >
                      <router-link
                        :to="{ name: 'contract_manager.detail', params: { id: cm.id } }"
                        target="_blank"
                      >
                        {{ cm.name }}
                      </router-link>
                    </div>
                  </template>
                  <CBadge v-else color="secondary">No</CBadge>
                </span>
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <!-- Account Settings (with toggles and actions) -->
        <CCol lg="7" md="6" class="mb-3">
          <CCard class="h-100">
            <CCardHeader>
              <CIcon name="cil-settings" size="sm" class="mr-2"/>
              <translate translate-context="customer_success.user_detail">Account Settings</translate>
            </CCardHeader>
            <CCardBody>
              <!-- Status Toggle -->
              <div class="setting-row">
                <div class="setting-info">
                  <span class="setting-label">Status</span>
                  <span class="setting-description text-muted small">
                    <translate translate-context="customer_success.user_detail">User can access their account</translate>
                  </span>
                </div>
                <div class="setting-control">
                  <CSwitch
                    color="success"
                    shape="pill"
                    :checked="user.is_active"
                    @update:checked="onToggleStatus"
                  />
                  <span class="setting-status" :class="user.is_active ? 'text-success' : 'text-danger'">
                    {{ user.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </div>
              </div>

              <!-- Dashboard Toggle -->
              <div class="setting-row">
                <div class="setting-info">
                  <span class="setting-label">Dashboard</span>
                  <span class="setting-description text-muted small">
                    <translate translate-context="customer_success.user_detail">Access to Pica Dashboard</translate>
                  </span>
                </div>
                <div class="setting-control">
                  <CSwitch
                    color="info"
                    shape="pill"
                    :checked="user.is_pica_dashboard_user"
                    @update:checked="onToggleDashboard"
                  />
                  <span class="setting-status" :class="user.is_pica_dashboard_user ? 'text-info' : 'text-muted'">
                    {{ user.is_pica_dashboard_user ? 'Enabled' : 'Disabled' }}
                  </span>
                </div>
              </div>

              <!-- Real Email Toggle -->
              <div class="setting-row">
                <div class="setting-info">
                  <span class="setting-label">
                    <translate translate-context="customer_success.user_detail">Real Email</translate>
                  </span>
                  <span class="setting-description text-muted small">
                    {{ isRealEmail
                      ? $pgettext('customer_success.user_detail', 'User will receive login token via email')
                      : $pgettext('customer_success.user_detail', 'User will login with password only')
                    }}
                  </span>
                </div>
                <div class="setting-control">
                  <CSwitch
                    color="success"
                    shape="pill"
                    :checked="isRealEmail"
                    @update:checked="onToggleRealEmail"
                  />
                  <span class="setting-status" :class="isRealEmail ? 'text-success' : 'text-warning'">
                    {{ isRealEmail ? 'Email vera' : 'Email virtuale' }}
                  </span>
                </div>
              </div>

              <!-- Password -->
              <div class="setting-row">
                <div class="setting-info">
                  <span class="setting-label">Password</span>
                  <span class="setting-description text-muted small">
                    <translate translate-context="customer_success.user_detail">Set a new password for the user</translate>
                  </span>
                </div>
                <div class="setting-control setting-control-input">
                  <CInput
                    v-model="reset.password"
                    type="text"
                    size="sm"
                    placeholder="New password..."
                    class="password-input"
                  />
                  <CButton
                    color="warning"
                    size="sm"
                    :disabled="!reset.password"
                    @click="onChangePassword"
                  >
                    <CIcon name="cil-lock-locked" size="sm" class="mr-1"/>
                    <translate translate-context="customer_success.user_detail">Set</translate>
                  </CButton>
                </div>
              </div>

              <!-- Email Change (if needed) -->
              <div class="setting-row">
                <div class="setting-info">
                  <span class="setting-label">
                    <translate translate-context="customer_success.user_detail">Change Email</translate>
                  </span>
                  <span class="setting-description text-muted small">
                    <translate translate-context="customer_success.user_detail">Update the user's email address</translate>
                  </span>
                </div>
                <div class="setting-control setting-control-input">
                  <CInput
                    v-model="newEmail"
                    type="email"
                    size="sm"
                    :placeholder="user.email"
                    class="email-input"
                  />
                  <CButton
                    color="primary"
                    size="sm"
                    :disabled="!newEmail || newEmail === user.email"
                    @click="onChangeEmail"
                  >
                    <CIcon name="cil-envelope-closed" size="sm" class="mr-1"/>
                    <translate translate-context="customer_success.user_detail">Set</translate>
                  </CButton>
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <!-- Second Row: Transactions & Checkins -->
      <UserInfo
        v-if="scopeReady"
        :user="user"
        :user-id="$route.params.id"
        :show-header="false"
      />

      <!-- Recent Disputes (only if any) -->
      <RecentChargebacks
        v-if="scopeReady"
        :user-id="$route.params.id"
        :scope="chargebackScope"
      />

      <!-- Collaborators -->
      <CRow v-if="canViewCollaborators">
        <CCol>
          <CCard>
            <CCardHeader class="d-flex align-items-center">
              <CIcon name="cil-people" size="sm" class="mr-2 text-info"/>
              <translate translate-context="customer_success.user_detail">Collaborations</translate>
              <CBadge v-if="collaborators.length > 0" color="info" class="ml-2">{{ collaborators.length }}</CBadge>
            </CCardHeader>
            <CCardBody class="p-0">
              <div v-if="loadingCollaborators" class="text-center py-4">
                <CSpinner size="sm"/>
              </div>

              <div v-else-if="collaborators.length === 0" class="text-muted text-center py-4">
                <CIcon name="cil-ban" size="xl" class="mb-2 d-block mx-auto"/>
                <translate translate-context="customer_success.user_detail">No collaborations found</translate>
              </div>

              <table v-else class="table table-sm table-borderless mb-0">
                <thead>
                  <tr>
                    <th><translate translate-context="customer_success.user_detail">Scope</translate></th>
                    <th><translate translate-context="customer_success.user_detail">Name</translate></th>
                    <th><translate translate-context="customer_success.user_detail">Roles</translate></th>
                    <th><translate translate-context="customer_success.user_detail">Status</translate></th>
                    <th><translate translate-context="customer_success.user_detail">Since</translate></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="collab in collaborators" :key="collab.id">
                    <td class="text-muted small">
                      {{ formatScopeType(collab.scope_type) }}
                    </td>
                    <td>
                      <router-link
                        v-if="collab.contract_manager_id"
                        :to="{ name: 'contract_manager.detail', params: { id: collab.contract_manager_id } }"
                        target="_blank"
                      >
                        {{ collab.scope_name }}
                      </router-link>
                      <router-link
                        v-else-if="collab.pica_service_id"
                        :to="{ name: 'eventWizard.date_and_name', params: { picaServiceId: collab.pica_service_id } }"
                        target="_blank"
                      >
                        {{ collab.scope_name }}
                      </router-link>
                      <span v-else>{{ collab.scope_name }}</span>
                    </td>
                    <td>
                      <span v-for="(role, idx) in collab.roles" :key="role.role_name">
                        {{ role.role_name }}<span v-if="role.expires_at" class="text-warning"> *</span><span v-if="idx < collab.roles.length - 1">, </span>
                      </span>
                    </td>
                    <td>
                      <span :class="getStatusClass(collab.status)">{{ collab.status }}</span>
                    </td>
                    <td class="text-muted small">
                      {{ formatShortDate(collab.created_at) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </div>

    <!-- Loading State -->
    <div v-else class="text-center py-5">
      <CSpinner/>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import UserInfo from '@/domain/accounting/components/UserInfo.vue'
import RecentChargebacks from '@/domain/accounting/chargeback/RecentChargebacks.vue'

export default {
  name: 'UserDetail',
  components: {
    UserInfo,
    RecentChargebacks
  },
  data() {
    return {
      reset: {
        password: ''
      },
      newEmail: '',
      scopeReady: false,
      loadingCollaborators: false,
      collaborators: []
    }
  },
  computed: {
    ...mapGetters('customerSuccess', ['user']),
    ...mapGetters('login', ['hasPerm']),
    isRealEmail() {
      return !this.user?.is_virtual
    },
    canImpersonate() {
      return this.hasPerm('dashboard.impersonate')
    },
    canViewCollaborators() {
      return this.hasPerm('admin.users.collaborators')
    },
    saasContractManagers() {
      const cm = this.user?.saas_contract_manager
      if (!cm) return []
      return Array.isArray(cm) ? cm : [cm]
    },
    chargebackScope() {
      return { type: 'global', id: null }
    }
  },
  async mounted() {
    await this.fetchUser(this.$route.params.id)
    await this.fetchConfig({ type: 'admin' })
    await this.setScope({ type: 'global' })
    this.scopeReady = true
    this.loadCollaborators()
  },
  methods: {
    ...mapActions('customerSuccess', [
      'fetchUser',
      'fetchCollaborators',
      'reactivate',
      'deactivate',
      'changePassword',
      'enableDashboard',
      'disableDashboard',
      'setVirtualUser',
      'unsetVirtualUser',
      'changeAccountEmail'
    ]),
    ...mapActions('accounting', ['setScope']),
    ...mapActions('impersonate', ['impersonate']),
    ...mapActions('login', ['fetchConfig']),

    async loadCollaborators() {
      if (!this.canViewCollaborators) return

      const userId = this.$route.params.id
      if (!userId) return

      this.loadingCollaborators = true
      try {
        const result = await this.fetchCollaborators(userId)
        this.collaborators = result || []
      } catch (error) {
        this.collaborators = []
      } finally {
        this.loadingCollaborators = false
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString()
    },

    formatShortDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString()
    },

    onChangePassword() {
      if (this.reset.password.length < 3) {
        return this.notifyError(this.$pgettext('customer_success.user_detail', 'Password must be at least 3 characters'))
      }
      this.changePassword(this.reset.password)
      this.reset.password = ''
      this.notifySuccess(this.$pgettext('customer_success.user_detail', 'Password changed'))
    },

    onToggleStatus(isActive) {
      if (isActive) {
        this.reactivate()
        this.notifySuccess(this.$pgettext('customer_success.user_detail', 'User activated'))
      } else {
        this.deactivate()
        this.notifySuccess(this.$pgettext('customer_success.user_detail', 'User deactivated'))
      }
    },

    onToggleDashboard(isEnabled) {
      if (isEnabled) {
        this.enableDashboard()
        this.notifySuccess(this.$pgettext('customer_success.user_detail', 'Dashboard access enabled'))
      } else {
        this.disableDashboard()
        this.notifySuccess(this.$pgettext('customer_success.user_detail', 'Dashboard access disabled'))
      }
    },

    async onToggleRealEmail(isRealEmail) {
      try {
        if (isRealEmail) {
          // Real email = unset virtual
          await this.unsetVirtualUser(this.user.id)
          this.notifySuccess(this.$pgettext('customer_success.user_detail', 'Email set as real'))
        } else {
          // Virtual email = set virtual
          await this.setVirtualUser(this.user.id)
          this.notifySuccess(this.$pgettext('customer_success.user_detail', 'Email set as virtual'))
        }
      } catch (error) {
        this.notifyError(this.$pgettext('customer_success.user_detail', 'Failed to update email type'))
      }
    },

    async onChangeEmail() {
      if (!this.newEmail || this.newEmail === this.user.email) return

      try {
        await this.changeAccountEmail({
          userId: this.user.id,
          email: this.newEmail
        })
        this.newEmail = ''
        this.notifySuccess(this.$pgettext('customer_success.user_detail', 'Email changed'))
        await this.fetchUser(this.$route.params.id)
      } catch (error) {
        this.notifyError(this.$pgettext('customer_success.user_detail', 'Failed to change email'))
      }
    },

    async onImpersonate() {
      await this.impersonate(this.user)
    },

    formatScopeType(scopeType) {
      const labels = {
        'CONTRACT_MANAGER': 'Contract Manager',
        'PICA_SERVICE': 'Pica Service'
      }
      return labels[scopeType] || scopeType
    },

    getStatusClass(status) {
      const classes = {
        'ACCEPTED': 'text-success',
        'PENDING': 'text-warning',
        'REJECTED': 'text-danger',
        'REMOVED': 'text-muted'
      }
      return classes[status] || 'text-muted'
    }
  }
}
</script>

<style scoped>
.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #ebedef;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  color: #768192;
  font-size: 0.875rem;
}

.detail-value {
  font-weight: 500;
  color: #3c4b64;
}

/* Setting rows for Account Settings card */
.setting-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #ebedef;
}

.setting-row:last-child {
  border-bottom: none;
}

.setting-info {
  display: flex;
  flex-direction: column;
}

.setting-label {
  font-weight: 500;
  color: #3c4b64;
  margin-bottom: 0.125rem;
}

.setting-description {
  font-size: 0.75rem;
}

.setting-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.setting-control-input {
  display: flex;
  gap: 0.5rem;
}

.setting-status {
  font-size: 0.75rem;
  font-weight: 500;
  min-width: 60px;
  text-align: right;
}

.password-input,
.email-input {
  width: 160px;
}


</style>
